import React from 'react'
import { AnimatePresence, motion } from 'motion/react'
import { X } from 'lucide-react'
import { CheckinNudge as CheckinNudgeData } from '../../api/client'

/**
 * The one presentational nudge.
 *
 * A nudge is a small dismissable offer attached to one checkin, rendered
 * directly beneath that checkin's row. This component renders exactly one, and
 * knows nothing about where nudges come from, how they are dismissed, or what
 * an action does — it takes `{ kind, payload, onAction, onDismiss }` and draws
 * the card. That is deliberate: the second `kind` (the parser's follow-up
 * questions) should be a case in `NUDGE_COPY` plus a handler in the page, not a
 * second component.
 *
 * `NudgeAction` carries `openGenie` even though no kind emits it yet, for the
 * same reason. The precedent is already in the codebase — `discussCheckinsInGenie`
 * POSTs to `/api/genie/chats/discuss-checkins` and navigates to `/genie/{id}` —
 * so a follow-up question becomes a prompt and a context object handed to that
 * path, and neither this component's props nor the nudges table changes shape.
 *
 * Visual reference: `components/project/EmptyProjectNudge.tsx`. Same amber
 * card, same surface pop-in (`opacity`/`scale: 0.95 → 1`/`y: -8 → 0` at
 * stiffness 500 / damping 30), same close-button idiom on the `×`
 * (`scale: 1.1, rotate: 90` on hover, `0.9` on tap, stiffness 400 / damping 20).
 * Nothing new is invented.
 */

const PUNCTUATION_RE = /[^\p{L}\p{N}_\s]/gu
const WHITESPACE_RE = /\s+/gu

/**
 * Mirror of `normalize_checkin_body` in
 * `backend/server/features/checkin_templates.py`: NFKC-fold, lowercase, turn
 * every non-word character into a space, collapse runs of whitespace.
 *
 * This is the one definition of "these two checkins say the same thing" — it
 * is what `checkin_templates.signature` and `checkin_nudges.signature` both
 * hold, so the two implementations agreeing is not cosmetic. Stage 6's
 * repetition judge short-circuits on exact normalized matches before it will
 * spend a model call, and a frontend that normalized differently would
 * disagree with the server about which checkins are the same text.
 *
 * `CheckinNudge.test.tsx` pins it against output taken from running the real
 * Python function rather than from reading it. Two properties matter most and
 * are asserted there: `"Took 1.2mg Amitriptyline."` and
 * `"took 1.2mg amitriptyline"` normalize the SAME, while
 * `"took 1.2mg amitriptyline"` and `"1.2 ami"` normalize DIFFERENTLY — the
 * latter pair is a judge's problem, not an exact-match one.
 *
 * The character classes are written out because JavaScript's `\w` is ASCII-only
 * where Python's is Unicode-aware; `[^\p{L}\p{N}_\s]` with the `u` flag is the
 * equivalent of Python's `[^\w\s]` under `re.UNICODE`.
 */
export const normalizeCheckinBody = (body: string | null | undefined): string => {
    if (!body) return ''
    const folded = body.normalize('NFKC').toLowerCase().replace(PUNCTUATION_RE, ' ')
    return folded.replace(WHITESPACE_RE, ' ').trim()
}

/** Raised by Duplicate: you just copied this, so you evidently repeat it. */
export const KIND_TEMPLATE_UPSELL = 'template_upsell'

/**
 * Raised by the repetition judge (Stage 6): nobody pressed anything, but the
 * same thing has been written three or more times. The payload carries an
 * `occurrences` count, which the copy deliberately does not print — "you've
 * added this repeatedly" is the whole point, and naming a number invites the
 * reader to audit it against a timeline whose entries are, by construction,
 * worded differently from each other. The count stays in the payload as data.
 */
export const KIND_REPETITION_UPSELL = 'repetition_upsell'

/**
 * Mirror of `NUDGE_PRIORITY` in `backend/server/features/checkin_nudges.py`.
 * Lower shows first. The server already sorts, but only one nudge is ever on
 * screen, so the choice of which is worth making locally too — the page
 * re-selects after a dismissal without a refetch.
 *
 * Duplicate outranks the judge because its evidence is better: the user
 * pressed something a moment ago, where the judge is volunteering a pattern
 * nobody asked about.
 */
const NUDGE_PRIORITY: Record<string, number> = {
    [KIND_TEMPLATE_UPSELL]: 10,
    [KIND_REPETITION_UPSELL]: 20,
}
const DEFAULT_PRIORITY = 100

/**
 * What a nudge's primary button does, once the page gets hold of it.
 *
 * `saveTemplate` carries no checkin id on purpose: the component is handed a
 * kind and a payload and nothing else, so the page — which knows which nudge it
 * chose to render — supplies the anchor.
 */
export type NudgeAction =
    | { type: 'saveTemplate' }
    | { type: 'openGenie'; prompt: string; context?: Record<string, unknown> }

interface NudgeCopy {
    /**
     * `label` is the payload's remembered checkin text, already trimmed.
     * `payload` is handed over too so a kind can use its own fields —
     * `repetition_upsell` names how many times it saw the thing — without
     * every kind needing to declare a parameter for it.
     */
    message: (label: string, payload: Record<string, unknown> | null) => React.ReactNode
    actionLabel: string
    action: NudgeAction
}

/* Neither kind quotes the checkin. The card renders directly beneath the row
   whose text it is talking about, so "this text" is unambiguous and a quotation
   would be the same words twice, three lines apart — and the quoted form had to
   be the 24-character label, which is the body with its end cut off.

   Both kinds make the same offer and take the same answer, so they end on the
   same sentence and the same button. Only the opening differs, because only
   the evidence differs: Duplicate reacts to a press, the judge reports what it
   noticed. "at the top" is doing work in that sentence — it is the one hint
   that the thing being offered lands in the pill row above the timeline. */
const KEEP_IT = 'Keep it as a one-tap shortcut at the top?'
const YES = 'Yes please'

const NUDGE_COPY: Record<string, NudgeCopy> = {
    [KIND_TEMPLATE_UPSELL]: {
        message: () => <>You’ve saved this text more than once. {KEEP_IT}</>,
        actionLabel: YES,
        action: { type: 'saveTemplate' },
    },
    [KIND_REPETITION_UPSELL]: {
        message: () => <>You’ve saved this text repeatedly. {KEEP_IT}</>,
        actionLabel: YES,
        action: { type: 'saveTemplate' },
    },
}

/**
 * The highest-priority nudge worth showing, or null.
 *
 * Skips kinds this build does not know how to draw, so a server that has
 * learned a new kind ahead of a deployed frontend degrades to showing the next
 * one down rather than to an empty card. Never mutates its argument.
 */
export const selectVisibleNudge = (
    nudges: CheckinNudgeData[],
): CheckinNudgeData | null => {
    const renderable = nudges.filter(
        nudge => nudge.state === 'active' && nudge.kind in NUDGE_COPY,
    )
    if (renderable.length === 0) return null
    return [...renderable].sort((a, b) => {
        const byPriority =
            (NUDGE_PRIORITY[a.kind] ?? DEFAULT_PRIORITY) -
            (NUDGE_PRIORITY[b.kind] ?? DEFAULT_PRIORITY)
        if (byPriority !== 0) return byPriority
        return b.id - a.id
    })[0]
}

/** `template_upsell`'s payload; other kinds will carry other fields. */
const payloadLabel = (payload: Record<string, unknown> | null): string => {
    const label = typeof payload?.label === 'string' ? payload.label.trim() : ''
    if (label) return label
    const body = typeof payload?.body === 'string' ? payload.body.trim() : ''
    return body
}

const payloadBody = (payload: Record<string, unknown> | null): string =>
    typeof payload?.body === 'string' ? payload.body : ''

interface CheckinNudgeProps {
    kind: string
    payload: Record<string, unknown> | null
    /**
     * The anchor checkin's body as it currently reads. When it is supplied and
     * no longer says what the payload remembers, the nudge renders nothing:
     * offering to save "ran 5k today" as a template is wrong the moment the
     * user has edited that checkin into something else. Compared through
     * `normalizeCheckinBody`, so fixing a typo or a capital does not silently
     * withdraw the offer.
     */
    anchorBody?: string | null
    onAction: (action: NudgeAction) => void
    onDismiss: () => void
    /** Set while the page is acting on this nudge; disables both buttons. */
    busy?: boolean
}

const CheckinNudge: React.FC<CheckinNudgeProps> = ({
    kind,
    payload,
    anchorBody,
    onAction,
    onDismiss,
    busy = false,
}) => {
    const copy = NUDGE_COPY[kind]
    if (!copy) return null

    const label = payloadLabel(payload)
    if (!label) return null

    if (
        anchorBody != null &&
        normalizeCheckinBody(anchorBody) !== normalizeCheckinBody(payloadBody(payload))
    ) {
        return null
    }

    return (
        <AnimatePresence>
            <motion.div
                key={`checkin-nudge-${kind}-${label}`}
                initial={{ opacity: 0, scale: 0.95, y: -8 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.95, y: -8 }}
                transition={{ type: 'spring', stiffness: 500, damping: 30 }}
                style={{ transformOrigin: 'top center' }}
                className="bg-amber-50 border border-amber-200 rounded-lg p-4 mt-3"
                data-testid="checkin-nudge"
                data-nudge-kind={kind}
            >
                <div className="flex items-start justify-between gap-2">
                    <p className="text-sm text-gray-800 min-w-0">{copy.message(label, payload)}</p>
                    <motion.button
                        type="button"
                        onClick={onDismiss}
                        disabled={busy}
                        whileHover={{ scale: 1.1, rotate: 90 }}
                        whileTap={{ scale: 0.9 }}
                        transition={{ type: 'spring', stiffness: 400, damping: 20 }}
                        className="text-gray-400 hover:text-gray-700 flex-shrink-0"
                        aria-label="Dismiss suggestion"
                        data-testid="checkin-nudge-dismiss"
                    >
                        <X size={16} />
                    </motion.button>
                </div>
                <div className="mt-3">
                    <motion.button
                        type="button"
                        onClick={() => onAction(copy.action)}
                        disabled={busy}
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                        transition={{ type: 'spring', stiffness: 400, damping: 20 }}
                        className="px-3 py-1 rounded-md bg-action text-plum text-sm hover:bg-action-dark disabled:opacity-50"
                        data-testid="checkin-nudge-action"
                    >
                        {copy.actionLabel}
                    </motion.button>
                </div>
            </motion.div>
        </AnimatePresence>
    )
}

export default CheckinNudge
